import { LoadingState } from "@/ds/custom/LoadingState";
import { StatePanel } from "@/ds/custom/StatePanel";
import { Button } from "@/ds/shadcn/components/ui/button";
import { Spinner } from "@/ds/shadcn/components/ui/spinner";
import { HeaderBar } from "@/features/common/components/HeaderBar";
import { HeaderBarTools } from "@/features/common/components/HeaderBarTools";
import { ListWithPreview } from "@/features/common/components/ListWithPreview";
import type { NotificationModel } from "@/features/notifications/api/models/NotificationModel";
import { useMarkAllNotificationsRead } from "@/features/notifications/api/useMarkAllNotificationsRead";
import { useNotifications } from "@/features/notifications/api/useNotifications";
import { useNotificationsUnreadCount } from "@/features/notifications/api/useNotificationsUnreadCount";
import { NotificationListItem } from "@/features/notifications/components/NotificationListItem";
import { NotificationPreviewDisplay } from "@/features/notifications/components/NotificationPreviewDisplay";
import { ErrorDisplay } from "@/lib/common/ErrorDisplay";
import { useNotifyMutationResult } from "@/lib/common/useNotifyMutationResult";
import { useTitle } from "@/lib/common/useTitle";
import React, { useMemo, useState, type FC } from "react";
import { BellOff } from "lucide-react";

export const PageNotifications: FC = () => {
	useTitle("Notifications");

	const breadcrumb = useMemo(() => [
		{ label: "Dashboard", href: "/dashboard" },
		{ label: "Notifications" }
	], []);

	const markAllNotificationsRead = useMarkAllNotificationsRead();
	useNotifyMutationResult(markAllNotificationsRead, "Notifications Marked as Read");
	const notificationsUnreadCount = useNotificationsUnreadCount();
	const unreadCount = notificationsUnreadCount.data ? notificationsUnreadCount.data.unreadCount : 0;

	return (
		<>
			<HeaderBar title="Notifications" description="View your notifications from the Fyendal's Collection platform" breadcrumb={breadcrumb} />
			<HeaderBarTools>
				<Button disabled={unreadCount === 0 || markAllNotificationsRead.isPending} className="@md:w-34" variant="outline" onClick={() => markAllNotificationsRead.mutate(undefined)}>
					{
						markAllNotificationsRead.isPending ? <Spinner /> : "Mark all as read"
					}
				</Button>
			</HeaderBarTools>

			<PageNotificationsInternal />
		</>
	);
};

const PageNotificationsInternal = (): React.JSX.Element => {
	const notifications = useNotifications();
	const notificationsItemsCount = useMemo(() => (notifications.data?.pages || []).flatMap(x => x.results).length, [notifications.data]);
	const [selectedNotification, setSelectedNotification] = useState<NotificationModel | null>(null);

	if (notifications.isError) {
		return (
			<div className="p-3">
				<ErrorDisplay error={notifications.error} />
			</div>
		);
	}

	if (notifications.isPending) {
		return <LoadingState />;
	}

	if (notificationsItemsCount === 0) {
		return <StatePanel icon={BellOff} title="No notifications found" description="You have no notifications at this time." />;
	}

	return (
		<ListWithPreview
			forQuery={notifications}
			value={selectedNotification}
			onChange={setSelectedNotification}
			getKey={item => item.notificationId}
			componentFunction={NotificationListItem}>

			{
				selectedNotification &&
				<NotificationPreviewDisplay notificationId={selectedNotification.notificationId} />
			}
		</ListWithPreview>
	);
};
